import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Info,
  Settings2,
  X,
  Calendar,
  ChevronDown,
  ChevronUp,
  Globe,
  Languages,
  User,
  Target,
  Bot,
  FileText,
  History,
  Pencil,
} from "lucide-react";
import MessageList from "../../components/MessageList/MessageList";
import ConversationHistoryDialog from "../../components/ConversationHistory/ConversationHistoryDialog";
import FeedbackModal from "../../components/FeedbackModal/FeedbackModal";
import CompletedTrainingModal from "../../components/CompletedTrainingModal/CompletedTrainingModel";
import CustomModal from "../../components/CustomModal/CustomModal";
import Dropdown from "../../components/Dropdown/Dropdown";
import Badge from "../../components/Badge/Badge";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import FeedbackLoadingOverlay from "../../components/Loading/FeedbackLoadingOverlay";
import { updatePracticeMeeting } from "../../api/meetingApi";

import { pickBadgeColors } from "../../utils/pickBadgeColors";

import useMeetingController from "./hooks/useMeetingController";
import useAudioPulseCssVar from "./hooks/useAudioPulseCssVar";
import MeetingDetailsEditor from "./components/MeetingDetailsEditor";

import "./MeetingRoom.css";
import { formatNice, pretty } from "../../utils/formatNice";
import {
  formatRemainingSecondsLabel,
  isBetaRealtimeEntitlement,
  isRealtimeLimitedEntitlement,
  isRealtimeMinutesEntitlement,
} from "../../utils/realtimeEntitlements";
import MeetingRoomControls from "./components/MeetingRoomControls/MeetingRoomControls";
import { PracticeAssistToggle } from "./components/PracticeAssist/PracticeAssist";

export default function MeetingRoom({ onBack }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id: routeMeetingId } = useParams();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [exitInProgress, setExitInProgress] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const transcriptSectionRef = useRef(null);
  // Assuming `state.trainingPlanId` contains the training plan ID
  const { trainingPlanId } = state || {}; 
  const isTrainingMode = Boolean(trainingPlanId); 
  const userEntitlements = useSelector((s) => s.user?.data?.entitlements);

  // 🔹 Central orchestrator
  const ctrl = useMeetingController({ routeMeetingId, state, onBack, isTrainingMode: isTrainingMode, trainingPlanId });
  
  const { meetingData, trainingSession, audio } = ctrl;
  


  const meetingTypeLabel = formatNice(
    meetingData?.customMeeting || meetingData?.meetingType || "Meeting"
  );

  // Industry / Role / Speaking With (no custom fields anymore)
  const industryLabel = pretty(meetingData?.industry);
  const roleLabel = pretty(meetingData?.userRole);
  const speakingWithLabel = pretty(meetingData?.speakingWith);
  const aiPartnerLabel = isTrainingMode ? "Telome Coach" : speakingWithLabel || "AI Partner";
  const languageLabel = meetingData?.languageLabel || "English";

  // Behavior
  const behaviorLabel = formatNice(meetingData?.aiBehavior || "N/A");

// Goals (prettify each)
const allGoals = [
  ...(Array.isArray(meetingData?.goals) ? meetingData.goals : [meetingData?.goals]).filter(Boolean),
  ...(Array.isArray(meetingData?.customGoals) ? meetingData.customGoals : [meetingData?.customGoals]).filter(Boolean),
].map(g => formatNice(String(g)));
  
  // Apply badge colors to behavior
  const style = pickBadgeColors(behaviorLabel);
  const sessionStartRemainingMinutesRef = useRef(null);
  const autoEndedRef = useRef(false);
  const isRealtimeLimitedPlan = isRealtimeLimitedEntitlement(userEntitlements);
  const isBetaRealtimePlan = isBetaRealtimeEntitlement(userEntitlements);

  useEffect(() => {
    if (!ctrl.sessionActive) {
      sessionStartRemainingMinutesRef.current = null;
      autoEndedRef.current = false;
      return;
    }
    if (sessionStartRemainingMinutesRef.current == null) {
      sessionStartRemainingMinutesRef.current = Number(userEntitlements?.remainingMinutes || 0);
    }
  }, [ctrl.sessionActive, userEntitlements?.remainingMinutes]);

  const remainingSeconds = useMemo(() => {
    if (userEntitlements?.isUnlimited) return null;

    const baseMinutes = ctrl.sessionActive
      ? (sessionStartRemainingMinutesRef.current ?? Number(userEntitlements?.remainingMinutes || 0))
      : Number(userEntitlements?.remainingMinutes || 0);

    const consumedMinutes = ctrl.sessionActive ? (ctrl.elapsed / 60) : 0;
    const minsLeft = Math.max(0, baseMinutes - consumedMinutes);
    return Math.max(0, Math.round(minsLeft * 60));
  }, [ctrl.elapsed, ctrl.sessionActive, userEntitlements?.isUnlimited, userEntitlements?.remainingMinutes]);

  const remainingMinutesLabel = isBetaRealtimePlan
    ? "Beta"
    : formatRemainingSecondsLabel(remainingSeconds, {
        unlimited: Boolean(userEntitlements?.isUnlimited),
        suffix: "left",
      });
  const betaMinutesInfoLabel = formatRemainingSecondsLabel(remainingSeconds, {
    suffix: "left today",
  });
  const entitlementBadgeTitle = isBetaRealtimePlan
    ? "Beta realtime access"
    : userEntitlements?.isUnlimited
      ? "Unlimited realtime access"
      : "Realtime minutes remaining this period";
  const entitlementBadgeAriaLabel = isBetaRealtimePlan
    ? "Beta realtime access"
    : userEntitlements?.isUnlimited
      ? "Realtime access: Unlimited"
      : `Realtime minutes remaining: ${remainingMinutesLabel}`;
  const entitlementBadgeStateClass = userEntitlements?.isUnlimited
    ? "mr-timer-pill--unlimited"
    : (isRealtimeLimitedPlan && Number(remainingSeconds || 0) <= 0
        ? "mr-timer-pill--exhausted"
        : "");
  const isEntitlementExhausted = isRealtimeLimitedPlan && Number(remainingSeconds || 0) <= 0;

  useEffect(() => {
    if (!ctrl.sessionActive || !isRealtimeLimitedPlan) return;
    if (remainingSeconds > 0) return;
    if (autoEndedRef.current) return;

    autoEndedRef.current = true;
    ctrl.handleEndSession?.({ force: true, reason: "entitlement_exhausted" });
  }, [ctrl, ctrl.sessionActive, isRealtimeLimitedPlan, remainingSeconds]);

  useAudioPulseCssVar({
    targetRef: transcriptSectionRef,
    volumeRef: ctrl.assistantVolumeRef,
    enabled: ctrl.sessionActive,
    cssVar: "--mr-ai-pulse",
    gate: 0.015,
  });

  // Fallback loading
  if (!meetingData) return <LoadingOverlay title="Loading" />;

  const requestExitWithoutResults = () => {
    if (exitInProgress) return;
    setExitModalOpen(true);
  };

  const cancelExitWithoutResults = () => {
    if (exitInProgress) return;
    setExitModalOpen(false);
  };

  const confirmExitWithoutResults = async () => {
    if (exitInProgress) return;
    setExitInProgress(true);
    try {
      if (ctrl.sessionActive || ctrl.isConnecting || ctrl.isLoading) {
        await ctrl.handleEndSession?.({
          force: true,
          skipFeedback: true,
        });
      }
      setExitModalOpen(false);
      navigate("/conversation-type", { replace: true });
    } finally {
      setExitInProgress(false);
    }
  };

  const handleSaveMeetingDetails = async (nextMeetingData) => {
    if (!meetingData?.id) return;
    setIsSavingDetails(true);
    try {
      const payload = {
        meetingType: nextMeetingData?.meetingType,
        industry: nextMeetingData?.industry,
        userRole: nextMeetingData?.userRole,
        speakingWith: nextMeetingData?.speakingWith,
        languageLabel: nextMeetingData?.languageLabel || "English",
        goals: Array.isArray(nextMeetingData?.goals) ? nextMeetingData.goals : [],
        customGoals: Array.isArray(nextMeetingData?.customGoals) ? nextMeetingData.customGoals : [],
        aiBehavior: nextMeetingData?.aiBehavior,
        duration: Number(nextMeetingData?.duration),
        aiVoice: nextMeetingData?.aiVoice || meetingData?.aiVoice || "cedar",
        additionalInfo: nextMeetingData?.additionalInfo || "",
        documentContext: nextMeetingData?.documentContext || "",
        documents: Array.isArray(nextMeetingData?.documents) ? nextMeetingData.documents : [],
      };

      const persistedMeeting = await updatePracticeMeeting(meetingData.id, payload);
      ctrl.setMeetingData?.(persistedMeeting);

      const canApplyLive =
        Boolean(ctrl.sessionActive) &&
        Boolean(ctrl.wsReady) &&
        typeof ctrl.applyInstructions === "function";
      let liveApplied = false;

      if (canApplyLive) {
        liveApplied = Boolean(
          ctrl.applyInstructions({
          meetingData: persistedMeeting,
          priorConvoRef: ctrl.convRef,
          voiceId: persistedMeeting?.aiVoice,
          sendEvent: ctrl.sendEvent,
          force: true,
          })
        );
        if (!liveApplied) {
          ctrl.resetInstructions?.();
        }
      } else {
        ctrl.resetInstructions?.();
      }

      ctrl.toast?.({
        title: canApplyLive && liveApplied ? "Instructions updated" : "Details saved",
        description: canApplyLive && liveApplied
          ? "Meeting details were saved and applied to the live session."
          : canApplyLive
            ? "Meeting details were saved. Live update will apply on the next instruction refresh."
            : "Meeting details were saved and will apply to the next session.",
        variant: canApplyLive && !liveApplied ? "warning" : "default",
      });

      setIsEditingDetails(false);
      const isMobileDrawer =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 900px)").matches;
      if (isMobileDrawer) {
        setDetailsOpen(false);
      }
    } catch (err) {
      const details = Array.isArray(err?.response?.data?.details)
        ? err.response.data.details
        : [];
      const detailMsg = details[0]?.message;
      ctrl.toast?.({
        title: "Failed to save details",
        description:
          detailMsg ||
          err?.response?.data?.error ||
          err?.message ||
          "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingDetails(false);
    }
  };

  // ─────────────────────────────
  // 🔹 Render
  // ─────────────────────────────
  return (
    <>
      <div
        className={`mr-container ${isTrainingMode ? "mr-container--training" : ""} ${ctrl.practiceAssistMessage ? "mr-container--assist-note" : ""}`.trim()}
      >
        <audio
          id="assistant-audio"
          autoPlay
          muted={false}
          playsInline
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: "none",
            left: "-9999px",
          }}
        />

        {/* MAIN CONTENT */}
        <div className="mr-main-content">
          <div className="mr-transcript-section" ref={transcriptSectionRef}>
          

            <div className="mr-header-row">
                {/* Header */}
            <button className="mr-header-icon-button mr-back-btn" onClick={requestExitWithoutResults} aria-label="Back">
              <ArrowLeft size={20} />
            </button>
              <div className="mr-header-right">
                {isRealtimeMinutesEntitlement(userEntitlements) ? (
                  <div
                    className={`mr-timer-pill ${entitlementBadgeStateClass}`.trim()}
                    role="status"
                    title={entitlementBadgeTitle}
                    aria-label={entitlementBadgeAriaLabel}
                  >
                    {remainingMinutesLabel}
                    {isBetaRealtimePlan ? (
                      <span
                        className="mr-timer-pill-info"
                        aria-label={betaMinutesInfoLabel}
                        tabIndex={0}
                      >
                        <Info size={14} aria-hidden="true" />
                        <span className="mr-timer-pill-tooltip" role="tooltip">
                          {betaMinutesInfoLabel}
                        </span>
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <button
                  aria-label="Open conversation history"
                  className="mr-header-icon-button mr-history-button"
                  disabled={!ctrl.conversation.length}
                  onClick={() => setHistoryOpen(true)}
                  title="Conversation history"
                  type="button"
                >
                  <History size={18} />
                </button>
                <button
                  className="mr-header-icon-button mr-details-toggle"
                  onClick={() =>
                    setDetailsOpen((v) => {
                      const next = !v;
                      if (!next) setIsEditingDetails(false);
                      return next;
                    })
                  }
                  disabled={isSavingDetails}
                  aria-expanded={detailsOpen}
                  aria-controls="mr-details-drawer"
                  title="Meeting details"
                >
                  {detailsOpen ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </button>
                {/* Audio Settings */}
                <div className="mr-gear-dropdown">
                  <button
                    className="mr-header-icon-button mr-gear-button"
                    onClick={audio.toggleAudioSettings}
                    title="Audio Settings"
                  >
                    <Settings2 size={18} />
                  </button>

                  {audio.showAudioSettings && (
                    <div className="mr-device-popover" role="dialog">
                      <div className="mr-device-popover-header">
                        <h4>Meeting Settings</h4>
                        <button
                          className="mr-device-close-button"
                          onClick={audio.toggleAudioSettings}
                          aria-label="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      {/* Microphone */}
                      <label className="mr-device-label">Microphone</label>
                      <Dropdown
                        options={audio.inputDevices.map((d) => ({
                          value: d.deviceId,
                          label: d.label || `Mic ${d.deviceId.slice(-4)}`,
                        }))}
                        value={audio.selectedInputId}
                        onChange={audio.setSelectedInputId}
                        placeholder="Select microphone"
                        className="mr-device-dropdown"
                        disabled={audio.isMobile || audio.inputDevices.length === 0}
                      />

                      {/* Mobile Info */}
                      {audio.isMobile && (
                        <p className="mr-device-info">
                          To switch microphones, use your phone’s settings.
                        </p>
                      )}

                      {/* Output */}
                      {!audio.isMobile && (
                        <>
                          <label className="mr-device-label" style={{ marginTop: "1rem" }}>
                            Speaker Output
                          </label>
                          <Dropdown
                            options={audio.outputDevices.map((d) => ({
                              value: d.deviceId,
                              label: d.label || `Output ${d.deviceId.slice(-4)}`,
                            }))}
                            value={audio.selectedOutputId}
                            onChange={audio.handleOutputChange}
                            placeholder="Select speaker"
                            className="mr-device-dropdown"
                            disabled={audio.outputDevices.length === 0}
                          />
                        </>
                      )}

                      {!audio.isMobile && audio.inputDevices.length === 0 && (
                        <p className="mr-device-info">
                          Microphone access required to show devices
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Conversation */}
            <div className="mr-transcript-content">
            <MessageList
              conversation={ctrl.conversation}
              showTyping={ctrl.sessionActive && !ctrl.endProposed}
              isTrainingMode={isTrainingMode}
              speakingWithLabel={speakingWithLabel}
              sessionTimeLabel={ctrl.formattedElapsed}
              voice={meetingData?.aiVoice}
            />

              {!!ctrl.endProposed && (
                <div className="mr-end-banner" role="status">
                  <div className="mr-end-text">
                    <div className="mr-end-title">Session ended</div>
                    <div className="mr-end-reason">{ctrl.endProposed.reason}</div>
                  </div>
                </div>
              )}
            </div>

            {!isTrainingMode && ctrl.sessionActive ? (
              <PracticeAssistToggle
                enabled={ctrl.assistModeEnabled}
                onToggle={ctrl.toggleAssistMode}
              />
            ) : null}

            <MeetingRoomControls
              sessionActive={ctrl.sessionActive}
              isConnecting={ctrl.isConnecting}
              isTrainingMode={isTrainingMode}
              trainingAnswerState={ctrl.trainingAnswerState}
              isAiSpeaking={ctrl.isAiSpeaking}
              isMuted={ctrl.isMuted}
              onStartSession={ctrl.handleStartSession}
              onToggleMute={ctrl.handleToggleMute}
              onEndSession={ctrl.handleEndSession}
              onStartTrainingAnswer={ctrl.startTrainingAnswer}
              onSubmitTrainingAnswer={ctrl.submitTrainingAnswer}
              onRestartTrainingAnswer={ctrl.restartTrainingAnswer}
              micVolumeRef={ctrl.micVolumeRef}
              sessionError={ctrl.sessionError}
              isEntitlementExhausted={isEntitlementExhausted}
              onUpgrade={() => navigate("/pricing")}
              practiceAssistMessage={ctrl.practiceAssistMessage}
              practiceAssistVersion={ctrl.practiceAssistVersion}
            />
          </div>

          {/* Summary Drawer */}
          <div
            className={`mr-summary-section ${detailsOpen ? "mr-summary-open" : ""}`}
            id="mr-details-drawer"
          >
            <div className="mr-summary-card">
              <div className="mr-card-header">
                <h3 className="mr-card-title">{trainingSession ? `${meetingTypeLabel}: ${trainingSession.title}` : formatNice(meetingTypeLabel)}</h3>
                {detailsOpen && (
                  <button
                    className="mr-close-details-modal-button"
                    onClick={() => {
                      if (isSavingDetails) return;
                      setDetailsOpen((v) => !v);
                      setIsEditingDetails(false);
                    }}
                    aria-expanded={detailsOpen}
                    aria-controls="mr-details-drawer"
                  >
                    <X size={20} style={{ color: "var(--foreground)" }} />
                  </button>
                )}
              </div>

              {/* Details */}
              <div className="mr-card-content">
                {isTrainingMode ? (
                  <>
                    <div className="mr-summary-item">
                      <Target className="mr-summary-icon" />
                      <div className="mr-summary-text">
                        <span className="mr-summary-label">Training Plan</span>
                        <span className="mr-summary-value">
                          {trainingSession?.title || "Training Session"}
                        </span>
                      </div>
                    </div>
                    <div className="mr-summary-item">
                      <FileText className="mr-summary-icon" />
                      <div className="mr-summary-text">
                        <span className="mr-summary-label">Purpose</span>
                        <span className="mr-summary-value">
                          {trainingSession?.details || "No training purpose provided."}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mr-summary-toolbar">
                      <button
                        type="button"
                        className="mr-edit-details-button"
                        disabled={isSavingDetails}
                        onClick={() => setIsEditingDetails((v) => !v)}
                      >
                        {!isEditingDetails && <Pencil aria-hidden="true" size={16} />}
                        {isEditingDetails ? "Back to Details" : "Edit Instructions"}
                      </button>
                    </div>

                    {isEditingDetails ? (
                      <MeetingDetailsEditor
                        meetingData={meetingData}
                        isSaving={isSavingDetails}
                        onCancel={() => {
                          if (isSavingDetails) return;
                          setIsEditingDetails(false);
                        }}
                        onSave={handleSaveMeetingDetails}
                      />
                    ) : (
                      <>
                        <div className="mr-summary-item">
                          <Calendar className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Conversation Topic</span>
                            <span className="mr-summary-value">{meetingTypeLabel}</span>
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <Globe className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Industry</span>
                            <span className="mr-summary-value">{industryLabel}</span>
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <User className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">My Role</span>
                            <span className="mr-summary-value">{roleLabel}</span>
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <User className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Speaking With</span>
                            <span className="mr-summary-value">{speakingWithLabel}</span>
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <Languages className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Language</span>
                            <span className="mr-summary-value">{languageLabel}</span>
                          </div>
                        </div>

                        <div className="mr-summary-item">
                          <Target className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Goal</span>
                            {allGoals.length === 0 ? (
                              <span className="mr-summary-value">—</span>
                            ) : (
                              <ul className="mr-goal-list">
                                {allGoals?.map((g, i) => (
                                  <li key={i} className="mr-summary-value">{g}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <Bot className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">AI Behavior</span>
                            <span className="mr-summary-value">
                              <Badge label={behaviorLabel} style={style} />
                            </span>
                          </div>
                        </div>
                        <div className="mr-summary-item">
                          <FileText className="mr-summary-icon" />
                          <div className="mr-summary-text">
                            <span className="mr-summary-label">Documents</span>
                            <span className="mr-summary-value">
                              {meetingData.documents?.length > 0
                                ? meetingData.documents.map((d) => d.name).join(", ")
                                : "None"}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Feedback loading overlay */}
        {ctrl.isLoading && <FeedbackLoadingOverlay text={ctrl.loadingMsg || null} />}
      </div>

      <ConversationHistoryDialog
        aiPartnerLabel={aiPartnerLabel}
        conversation={ctrl.conversation}
        onClose={() => setHistoryOpen(false)}
        open={historyOpen}
      />

      {/* Feedback / Completed modals */}
      {(ctrl.feedbackData || ctrl.feedbackError) && (
        <FeedbackModal
          meetingData={meetingData}
          feedback={ctrl.feedbackData?.feedback}
          trainingPlan={ctrl.feedbackData?.trainingPlan}
          recording={ctrl.feedbackData?.recording}
          recordingAnalysis={ctrl.feedbackData?.recordingAnalysis}
          streak={ctrl.feedbackData?.streak}
          error={ctrl.feedbackError}
          onClose={ctrl.clearFeedback}
        />
      )}

      {ctrl.completedPlan && (
        <CompletedTrainingModal
          plan={ctrl.completedPlan.plan}
          meeting={ctrl.completedPlan.meeting}
          streak={ctrl.completedPlan.streak}
          onClose={ctrl.clearCompletedPlan}
        />
      )}

      <CustomModal open={exitModalOpen} onClose={cancelExitWithoutResults}>
        <div className="mr-exit-modal" role="alertdialog" aria-modal="true">
          <h2>Leave session?</h2>
          <p> Are you sure you want to leave this session?</p>
          <div className="mr-exit-modal-actions">
            <button
              type="button"
              className="mr-exit-modal-button mr-exit-modal-button-primary"
              disabled={exitInProgress}
              onClick={() => {
                void confirmExitWithoutResults();
              }}
            >
              {exitInProgress ? "Ending..." : "Yes"}
            </button>
            <button
              type="button"
              className="mr-exit-modal-button mr-exit-modal-button-secondary"
              disabled={exitInProgress}
              onClick={cancelExitWithoutResults}
            >
              No
            </button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}
